import { hydrateFromObject } from '@lib/utils/hydrate';
import Task from '../Task';
import { TaskTypeDefinition } from '../types';

const TASKS_STATE_KEY = 'tasks';

/**
 * Reads and hydrates persisted tasks from localStorage.
 *
 * Retrieves serialized tasks, parses, and hydrates them using task type
 * definitions. Skips tasks missing `_id`, `_type`, or with unrecognized types.
 *
 * @param taskTypes - Task type definitions with optional `handleHydration`.
 * @returns Hydrated tasks or `null` if none are valid.
 */
export function readPersistedTasks(
  taskTypes: Record<string, TaskTypeDefinition>,
): Record<string, Task> | null {
  const state = localStorage.getItem(TASKS_STATE_KEY);
  const resultTasks: Record<string, Task> = {};
  if (state) {
    const parsedTasks = JSON.parse(state);

    // hydrate tasks
    Object.keys(parsedTasks).forEach((key) => {
      const parsedTask = parsedTasks[key];

      // if the parsedTask does not have _id property, skip it
      if (!parsedTask._id) {
        console.warn(
          `Reading persisted task does not have _id property`,
          JSON.stringify(parsedTask),
        );
        return;
      }

      // if the parsedTask does not have _type property, skip it
      if (!parsedTask._type) {
        console.warn(
          `Reading persisted task does not have _type property`,
          JSON.stringify(parsedTask),
        );
        return;
      }

      // find out taskType based on _type property
      const taskTypeKey = Object.keys(taskTypes).find((key) =>
        taskTypes[key].types.includes(parsedTask._type),
      );
      if (!taskTypeKey) {
        console.warn(`Task type for task ${parsedTask._id} not found`);
        return;
      }

      // get the taskType definition
      const taskType = taskTypes[taskTypeKey];

      // if the parsedTask type specifies as 'handleHydration' function, use
      // the handleHydration function to perform the hydration
      let hydratedTask: Task | null = null;
      if (taskType?.handleHydration) {
        hydratedTask = taskType.handleHydration(parsedTask, (task) => {
          return hydrateFromObject(Task, task, undefined, [{}]);
        });
      } else {
        hydratedTask = hydrateFromObject(Task, parsedTask, undefined, [{}]);
      }

      // if we got a hydrated task, add it to the result
      if (hydratedTask) {
        resultTasks[key] = hydratedTask;
      }
    });
  }
  if (Object.keys(resultTasks).length === 0) {
    return null;
  }

  return resultTasks;
}

/**
 * Persists a collection of tasks to local storage.
 *
 * @param tasks - A record where the keys are task identifiers and the values
 *                are `Task` objects. This data will be serialized to JSON and
 *                stored under a predefined key in local storage.
 */
export function writePersistedTasks(tasks: Record<string, Task>): void {
  localStorage.setItem(TASKS_STATE_KEY, JSON.stringify(tasks));
}
